import LoadingSpinner from '@/storybook/components/LoadingSpinner/LoadingSpinner';
import SectionTitle, { SectionTitleProps, SectionTitleSize } from '@/storybook/components/SectionTitle/SectionTitle';
import { TYPOGRAPHY_CLASSES } from '@/storybook/components/Text/Text';
import cx from 'classnames';
import { ReactNode } from 'react';
import styles from './WidgetCard.module.css';

export const WIDGET_CARD_SIZE: Record<SectionTitleSize, keyof typeof TYPOGRAPHY_CLASSES> = {
  L: 'h3',
  M: 'h4',
  S: 'h4',
} as const;

const WIDGET_CARD_HEADING_TO_SECTION_TITLE_SIZE: Record<'h2' | 'h3' | 'h4', SectionTitleSize> = {
  h2: 'L',
  h3: 'M',
  h4: 'S',
};

export enum WidgetCardAppearance {
  'primary' = 'primary',
  'secondary' = 'secondary',
  'tertiary' = 'tertiary',
  'none' = 'none',
}

export type WidgetCardProps<T = string> = SectionTitleProps<T> & {
  'isLoading'?: boolean;
  'appearance'?: keyof typeof WidgetCardAppearance;
  'size'?: keyof typeof WIDGET_CARD_SIZE;
  'heading'?: 'h2' | 'h3' | 'h4';
  'additionalContent'?: ReactNode;
  'additionalContentColumns'?: 1 | 2 | 3;
  'fillHeight'?: boolean;
  'children'?: ReactNode;
  'data-testid'?: string;
  'body-testid'?: string;
  'loading-testid'?: string;
  'className'?: string;
  'bodyClassName'?: string;
  'displayContent'?: boolean;
};

export default function WidgetCard<T>({
  isLoading,
  appearance = 'primary',
  size = 'L',
  heading = 'h2',
  title,
  description,
  toggle,
  dropdown,
  button,
  downLoadButton,
  additionalContent,
  additionalContentColumns = 1,
  fillHeight = false,
  'data-testid': dataTestId = 'widgetCard',
  'description-testid': descriptionTestId,
  'title-testid': titleTestId,
  'toggle-testid': toggleTestId,
  'dropdown-testid': dropdownTestId,
  'button-testid': buttonTestId,
  'download-button-testid': downloadTestId,
  'body-testid': bodyTestId,
  'loading-testid': loadingTestId,
  children,
  className,
  bodyClassName,
  displayContent = true,
  ctaContainerAdditionalStyles,
}: WidgetCardProps<T>) {
  return (
    <div
      className={cx(styles.widgetCardContainer, className)}
      data-card-appearance={appearance}
      data-card-size={size}
      data-fill-height={fillHeight}
      data-testid={dataTestId}
    >
      <section className={styles.widgetCardHeader}>
        <SectionTitle
          title={title}
          description={description}
          size={WIDGET_CARD_HEADING_TO_SECTION_TITLE_SIZE[heading]}
          toggle={toggle}
          dropdown={dropdown}
          button={button}
          downLoadButton={downLoadButton}
          data-testid={`${dataTestId}-section-title`}
          title-testid={titleTestId}
          description-testid={descriptionTestId}
          toggle-testid={toggleTestId}
          dropdown-testid={dropdownTestId}
          button-testid={buttonTestId}
          download-button-testid={downloadTestId}
          ctaContainerAdditionalStyles={ctaContainerAdditionalStyles}
        />
        {additionalContent && (
          <div
            className={styles.widgetCardAdditionalContent}
            data-testid={`${dataTestId}-additionalContent`}
            data-columns={additionalContentColumns}
          >
            {additionalContent}
          </div>
        )}
      </section>
      <section
        className={cx(styles.widgetCardBody, bodyClassName)}
        data-is-display-content={displayContent}
        data-testid={bodyTestId}
      >
        {isLoading && <LoadingSpinner className={styles.widgetIsLoading} testId={loadingTestId} hideLoadingMessage />}
        {!isLoading && children}
      </section>
    </div>
  );
}
